import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { OrderService } from 'src/app/home/order/order.service';
import { CartService } from 'src/app/core/cart/cart.service';
import { SessionStorageService } from 'src/app/shared/storage/session-storage.service';
import { PopupMessageService } from 'src/app/core/services/popup-message.service';
import { StaticQrService } from 'src/app/core/static-qr/static-qr.service';
import { PreviousRouteService } from 'src/app/core/services/previous-route.service';
import { UserService } from 'src/app/core/user/user.service';
import { CheckoutOrderRequest } from 'src/app/core/models/CheckoutOrderRequest';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorCode } from 'src/app/core/enums/ErrorCode';

export interface CheckoutHandlerCallbacks {
    onOrderPaid: (isPaid: boolean) => void;
    onPaymentStatus?: (req: CheckoutOrderRequest) => void;
    onInsufficientBalance?: (insufficientBalance: number) => void;
    onOrderReqLogin?: () => void;
    onRequiredTableNo?: () => void;
}

@Injectable({
    providedIn: 'root'
})
export class CheckoutPaymentService {

    constructor(
        private orderService: OrderService,
        private cartService: CartService,
        private router: Router,
        private sessionStorageService: SessionStorageService,
        private popupMessageService: PopupMessageService,
        private staticQrService: StaticQrService,
        private previousRouteService: PreviousRouteService,
        private userService: UserService
    ) { }

    async paymentHandle(
        checkoutOrderRequest: CheckoutOrderRequest,
        isLoggedIn: boolean,
        memberEwalletBalance: number,
        callbacks: CheckoutHandlerCallbacks,
        forceCheckoutFlag: boolean = false
    ) {
        checkoutOrderRequest.forceCheckOut = forceCheckoutFlag;
        let response = await this.orderService.requestCheckOut(checkoutOrderRequest);

        if (response instanceof HttpErrorResponse) {
            if (response?.error?.errorCode == ErrorCode.OrderPaid_400) {
                callbacks.onOrderPaid(true);
            } else if (response?.error?.errorCode == ErrorCode.CartNotFound_404) {
                callbacks.onOrderPaid(false);
            } else if (response?.error?.errorCode == ErrorCode.OngoingPendingPayment_400) {
                if (callbacks.onPaymentStatus) {
                    callbacks.onPaymentStatus(checkoutOrderRequest);
                }
            } else if (response?.error?.errorCode == ErrorCode.OrderProcessing_400) {
                this.popupMessageService.show({
                    icon: "oda-remove-circle",
                    iconColor: "red",
                    title: "qr.payment.failed.title",
                    desc: "qr.payment.failed.desc",
                    btn: "button.ok",
                    redirectLink: '/order-summary'
                });
            } else if (response?.error?.errorCode == ErrorCode.UnauthorizeAccess_401) {
                console.log('checkout order failed: ', response);
            } else if (response?.error?.errorCode == ErrorCode.MinOrderAmt_400) {
                this.popupMessageService.show({
                    icon: "oda-remove-circle",
                    iconColor: "red",
                    title: "alert.payment.summary.order.des.4",
                    desc: response?.error?.title,
                    btn: "button.retry"
                });
            } else if (response?.error?.errorCode == ErrorCode.OrderItemBalanceQtyInvalid_400) {
                let backRoute = this.previousRouteService.getPaymentBackUrl();
                if (!backRoute) backRoute = '/cart';
                this.popupMessageService.show({
                    icon: "oda-remove-circle",
                    iconColor: "red",
                    title: "item.unavailable",
                    desc: "exceed.balance.description",
                    btn: "adjust.cart",
                    redirectLink: backRoute,
                });
            } else if (response?.error?.errorCode == ErrorCode.ItemBlockedNotSync_204) {
                let backRoute = this.previousRouteService.getPaymentBackUrl();
                if (!backRoute) backRoute = '/cart';
                this.popupMessageService.show({
                    icon: "oda-remove-circle",
                    iconColor: "red",
                    title: "item.unavailable",
                    desc: "block.item.description",
                    btn: "adjust.cart",
                    redirectLink: backRoute,
                });
            } else if (response?.error?.errorCode == ErrorCode.InvalidZeroAmtCheckout) {
                let backRoute = this.previousRouteService.getPaymentBackUrl();
                if (!backRoute) backRoute = '/cart';
                this.popupMessageService.show({
                    icon: "oda-remove-circle",
                    iconColor: "red",
                    title: "invalid.total.amount",
                    desc: "invalid.total.amount.desc",
                    btn: "adjust.cart",
                    redirectLink: backRoute,
                });
            } else if (response?.error?.errorCode == ErrorCode.RequiredTableNo_400) {
                this.sessionStorageService.setItem("requiredTableNo", JSON.stringify(true));
                this.router.navigate(['cart']);
                if (callbacks.onRequiredTableNo) {
                    callbacks.onRequiredTableNo();
                }
            } else if (response?.error?.errorCode == ErrorCode.OrderReqLogin_401) {
                this.userService.setShowContinueAsGuestFlag(false);
                this.sessionStorageService.setItem("fromVoucher", JSON.stringify({ fromVoucher: true }));
                this.sessionStorageService.setItem("navigateToPayment", JSON.stringify({ toPayment: true }));
                this.popupMessageService.show({
                    icon: "oda-remove-circle",
                    iconColor: "red",
                    title: "alert.cart.title",
                    desc: "alert.cart.description",
                    btn: "login.register.form.header.title.2",
                    redirectLink: '/login',
                });
                if (callbacks.onOrderReqLogin) {
                    callbacks.onOrderReqLogin();
                }
            } else if (response?.error?.errorCode == ErrorCode.MerchantWalletPaymentFailed_400) {
                let insufficientBalance = checkoutOrderRequest.netTotal - memberEwalletBalance;
                if (callbacks.onInsufficientBalance) {
                    callbacks.onInsufficientBalance(insufficientBalance);
                }
            } else if (response?.error?.errorCode == ErrorCode.PaymentBelowMinPayAmt_400) {
                this.popupMessageService.show({
                    icon: "oda-remove-circle",
                    iconColor: "red",
                    title: "paymentBelowMinPayAmt_400.title",
                    desc: response?.error?.title,
                    btn: "button.ok",
                });
            } else if (response.status != 401) {
                this.popupMessageService.show({
                    icon: "oda-remove-circle",
                    iconColor: "red",
                    title: "alert.payment.summary.order.des.4",
                    desc: "alert.payment.summary.order.des.5",
                    btn: "button.retry"
                });
            }
        } else if (response && response.isSuccess === false && response.isSuccess !== undefined) {
            this.popupMessageService.show({
                icon: "oda-remove-circle",
                iconColor: "red",
                title: "alert.payment.summary.order.des.4",
                desc: "alert.payment.summary.order.des.5",
                btn: "button.retry"
            });
        } else if (response && response.isSuccess === true) {
            if (this.staticQrService.get() && this.staticQrService.get().tableNo) {
                let staticQrData = localStorage.getItem("staticQrOrderSummary") ? JSON.parse(localStorage.getItem("staticQrOrderSummary")) : [];
                let todayDate = new Date();
                staticQrData.push({
                    'orderTranIds': response.orderTranIds,
                    'exp': todayDate.setDate(todayDate.getDate() + 1),
                });
                localStorage.setItem("staticQrOrderSummary", JSON.stringify(staticQrData));
            }

            if (!isLoggedIn) {
                this.cartService.upsertCart(response.payTranId);
            }

            if (response.checkoutUrl != null && response.checkoutUrl) {
                window.location.href = response.checkoutUrl;
            } else {
                try {
                    if (response.payTranId) {
                        let data = { 'payTranId': response.payTranId };
                        this.sessionStorageService.setItem("orderSummary", JSON.stringify(data));
                    } else {
                        this.cartService.clearCart();
                        let data = { 'orderTranIds': response.orderTranIds };
                        this.sessionStorageService.setItem("orderPayAtCounter", JSON.stringify(data));
                    }
                } catch (e) {
                    console.log("Failed to set session storage", e);
                }

                this.router.navigate(['/order-summary']);
            }
        } else {
            this.popupMessageService.show({
                icon: "oda-remove-circle",
                iconColor: "red",
                title: "alert.payment.summary.order.des.4",
                desc: "alert.payment.summary.order.des.5",
                btn: "button.retry"
            });
        }
    }
}
